import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Skip the auth probe on public pages — session is only relevant under /care-portal
    // Avoids a stray 401 request/log on every public page load.
    if (typeof window !== "undefined" && !window.location.pathname.startsWith("/care-portal") && !window.location.pathname.startsWith("/admin") && !window.location.pathname.startsWith("/chaplain-portal")) {
      setLoading(false);
      return;
    }
    // Probe session via httpOnly cookie; cookie is sent automatically with withCredentials
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password, surface) => {
    const res = await api.post("/auth/login", { email, password, ...(surface ? { surface } : {}) });
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Logout must always clear local state even if the network call fails
      // (e.g., offline, server down). The httpOnly cookie will expire on its own.
      void err;
    }
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, loading, login, logout }), [user, loading, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
